import {
  ContentTemplate,
  ContentType,
  IContentCalloutTemplate,
  IContentTextTemplate,
  LayoutBlock,
  LayoutBlockType,
  SubTaskDenormalized
} from '@dadohr/shared/domainTypes/experience';
import { useMemo } from 'react';
import { Content } from '../../../../../../../01-standalone-app/features/Experience/components/Task';
import { truncateMarkdown } from '../../../../../../../components/MarkdownText';

const calloutToTextBlock = (
  b: IContentCalloutTemplate
): IContentTextTemplate => {
  return {
    id: b.id,
    type: ContentType.TEXT,
    data: {
      t: b.data.t
    },
    parentLayoutElement: b.parentLayoutElement
  };
};

const useFirstTextBlock = (
  blocks: (ContentTemplate | SubTaskDenormalized | LayoutBlock)[]
): IContentTextTemplate | null => {
  return useMemo(() => {
    for (const b of blocks) {
      // We want a text block here.
      // We can "transform" some other related types like callouts into a text block for this as well
      // We can also dig into columns to find a text-like block.
      if (b.type === ContentType.TEXT) {
        return b;
      }
      if (b.type === ContentType.CALLOUT) {
        return calloutToTextBlock(b);
      }
      if (b.type === LayoutBlockType.COLUMNS) {
        // The inner column blocks should also be in the normal block list.
        // I am not sure if the sort order is guaranteed to be correct though.
        // If it were, we would't need to do anything here (correct would mean: the column block is followed by its inner blocks)
        // Let's play it safe and look them up manually
        for (const column of b.data.columns) {
          const columnBlock = blocks.find((b) => b.id === column.blockId);
          if (columnBlock?.type === ContentType.TEXT) {
            return columnBlock;
          }
          if (columnBlock?.type === ContentType.CALLOUT) {
            return calloutToTextBlock(columnBlock);
          }
        }
      }
    }
    return null;
  }, [blocks]);
};

export const TruncatedContentSummary = ({
  blocks,
  limit = 100
}: {
  blocks: (ContentTemplate | SubTaskDenormalized | LayoutBlock)[];
  limit?: number;
}) => {
  const block = useFirstTextBlock(blocks);
  const truncatedBlock = useMemo(() => {
    return block
      ? {
          ...block,
          data: {
            ...block.data,
            t: truncateMarkdown(block.data.t, limit)
          }
        }
      : null;
  }, [block]);
  if (!truncatedBlock) {
    return null;
  }

  return <Content d={truncatedBlock} />;
};
